/**
 * Sam Assistant chat helpers — the on-device stub router, the output
 * sanitizer, and the timeout wrapper `chatRouter.ts` uses around the AIOS
 * `/api/chat` call.
 */
import type { ChatRouteInput, ChatRouteOutput } from './types';

// ─── Chat agent (Sam Assistant) ────────────────────────────────────────────
/**
 * Rule-based stub router. Runs entirely on-device when the AIOS chat
 * endpoint is unreachable, times out, or returns malformed output. It's
 * intentionally narrow — keyword classification only — but it returns
 * useful routes so the widget never sits broken. Also serves the instant
 * first-open greeting (no model round-trip).
 */
// ────────────────────────────────────────────────────────────────────────
// Stub router intent vocabularies. Each set lists the keywords + phrases
// that map a visitor message to a specific specialist. Centralized at the
// top so adding synonyms is a one-line change instead of editing regexes
// scattered through the routing function. Order of detection matters —
// escalation is checked first, then high-signal pricing/timeline/process
// questions, then specialist intents, then fallback.
// ────────────────────────────────────────────────────────────────────────

const RX = {
  // Explicit human / sales / urgency — handle before everything else so a
  // visitor in distress isn't routed to a service spiel.
  escalation:
    /\b(human|real person|real human|talk to (sam|samuel|someone|a person)|representative|sales (rep|person)|i need help|urgent|emergency|asap|right away|frustrated|confused|stuck|can'?t figure|nothing is working|don'?t know what|speak to|reach (you|sam|samuel))\b/,

  // "How long does X take?" — answered with concrete project timelines.
  timeline:
    /\b(how long|how soon|timeline|turnaround|delivery|when (will|can|could)|how fast|how quick|deadline|by next (week|month)|ship date|launch (date|by))\b/,

  // "How does the process work?" — surfaces the 4-step Audit/Blueprint/
  // Build/Optimize loop instead of routing to a service.
  process:
    /\b(your process|how do you work|how does (this|that|it) work|what'?s the process|workflow|step by step|engagement model|how do we (start|begin)|first step|kickoff|onboarding)\b/,

  // Generic pricing / "how much" — answered as a price comparison rather
  // than punting to a single service.
  pricing:
    /\b(how much|how expensive|cost|costs|costing|pricing|prices|priced|priceing|budget|afford|ballpark|estimate|quote|quotes)\b/,

  // Booking / scheduling — surfaces both Cal.com flows.
  booking: /\b(book|booking|schedule|set up a (call|meeting|time)|cal\.com|calendly|when (are|can) you (free|available)|jump on a call|hop on a call|chat with you|meeting)\b/,

  // Location / where based / area served — Mooresville / Charlotte / Lake
  // Norman / remote — explicit GEO-targeted question.
  location:
    /\b(where (are|do) you|based|located|location|in person|on site|on-site|remote|local|near me|charlotte|mooresville|lake norman|cornelius|davidson|huntersville|concord|statesville|north carolina|nc\b)/,

  // ── AIOS (the flagship AI Operating Foundation) ─────────────────────────
  // Distinctive AIOS terms only, so generic "ai agent"/"automation" still
  // route to the AI Agents specialist. Checked before the studio + AI
  // agents branches in stubRouteChat.
  aios:
    /\b(auraos|aios|ai operating foundation|ai operating system|ai[- ]os\b|operating system for (your|my|our)|ai staff|approval queue|ai foundation|files? its work|back office (staffed|run by))\b/,

  // ── Studio (finished products) ──────────────────────────────────────
  studio:
    /\b(legallio|legal lio|crm|brandcentral crm|finished product|finished products|ready[- ]?made|off the shelf|saas|software as a service|already built|ready to use|existing app|existing software|use one of your|your products|your apps|app you (already|have) built|product you (already|have) built|studio|in[- ]house product)\b/,

  // Legal-doc subset of studio → LegalLio specifically.
  legallio:
    /\b(legallio|legal lio|legal doc|legal document|legal documents|nda|ndas|non[- ]disclosure|privacy policy|privacy policies|terms of service|tos|t&c|contract|contracts|lease|operating agreement|cease and desist|llc agreement|founders agreement|llc paperwork|i need a doc|need legal|need a lawyer|cheap lawyer|without a lawyer|legal paperwork|legaltech|legal tech)\b/,

  // CRM subset of studio → BRANDCENTRAL CRM specifically.
  crm:
    /\b(crm|brandcentral crm|sales (tool|software|crm)|pipeline|deal pipeline|deal flow|leads? (tracker|tracking|management)|customer relationship|hubspot|salesforce|pipedrive|attio|zoho|monday(?:\.com)?|notion crm|airtable crm|google sheets crm|sheet crm|spreadsheet crm)\b/,

  // Content Studio (AuraOS): videos, posts, carousels, posting.
  studioPrice: /\bcontent studio\b.*\b(cost|price|pricing|how much)\b|\b(cost|price|pricing|how much)\b.*\bcontent studio\b/,
  studioPost:
    /\b(post (for me|to|on|my)|posting to|publish(es|ing)? to|auto[- ]?post|youtube|tiktok|instagram reels?|post my (videos?|content))\b/,
  studioEdit:
    /\b(edit (my|our) videos?|video edit(ing|or)?|long videos? into shorts|(turn|cut) my (long )?videos?|cut shorts|make shorts|shorts from)\b/,
  studioCarousel: /\b(carousels?|linkedin posts?)\b/,
  studioYoutube:
    /\b((clip|cut|chop|repurpose)\b.*\b(youtube|twitch|channel)|youtube (link|url|video|videos|channel)|twitch|my (youtube )?channel)\b/,
  studio2: /\bcontent studio\b/,

  // Counts: deployed vs roles vs workflows.
  counts:
    /\b(how many (ai )?(staff|agents|roles|workflows)|number of (ai )?(staff|agents|roles|workflows))\b/,

  // Dashboard question.
  dashboard:
    /\b(my dashboard|the dashboard|look like this|portal look|what does (the|my) (portal|dashboard))\b/,

  // Re-skin / host-anywhere questions.
  reskin:
    /\b(re-?skin|redo my (website|site)|redesign my (website|site)|host (my|our|the) (site|website)|hosting|webflow|framer|shopify|next\.?js)\b/,

  // ── Operations consulting ──────────────────────────────────────────
  ops: /\b(consult|consulting|consultant|advisor|advisory|advice|operations consulting|ops audit|workflow|workflows|bottleneck|bottlenecks|process audit|review my|figure out|streamline|inefficien|too many tools|stack consolidation|consolidate (our|my) (tools|stack)|busywork|overgrown|messy|chaos|firefight|firefighting|hair[- ]on[- ]fire|process review|where to start|where do we start|where do i start|holding (us|me) back|drowning in|too manual|manual work)\b/,

  // ── AI agents ──────────────────────────────────────────────────────
  aiAgents:
    /\b(ai agent|ai agents|intelligent agent|intelligent agents|agent (build|builds|development)|chatbot|chat bot|chatbots|automation|automations|automated|llm|llms|gpt|gpt-?\d|claude(?! code)|gemini|llama|mistral|deepseek|qwen|cursor|lovable|fine[- ]tuning|rag|retrieval augmented|vector (db|database)|embeddings|prompt engineering|claude agent sdk|build me (an|a) (agent|bot)|i want (an|a) (agent|bot)|need (an|a) (agent|bot)|24\/7 monitoring|always on)\b/,

  // ── App development ────────────────────────────────────────────────
  appDev:
    /\b(app development|app develop|build (an?|me an?) app|mobile app|mobile apps|web app|web apps|native app|ios|iphone|ipad|android|react native|expo|swift|swiftui|kotlin|flutter|cross[- ]platform|cross platform|mvp|minimum viable|saas (app|build)|mobile build|app store|play store|tablet app|electron app|desktop app|api build|backend build)\b/,

  // ── Web design ─────────────────────────────────────────────────────
  webDesign:
    /\b(web design|web designer|website|websites|webite|websight|web sight|site|sites|landing page|landing pages|homepage|home page|web page|web pages|webpage|webpages|seo|on[- ]page seo|local seo|search engine|site redesign|redo my site|rebuild (my|our) (site|website)|new (site|website)|wordpress (replacement|alternative)|squarespace (replacement|alternative)|wix (replacement|alternative)|webflow|framer|page count|per page|\$\s*450|450 per page|450\/page|page pricing|brochure site|portfolio site|marketing site)\b/,

  // ── Apps & Products generic — not a specific product, but interested
  // in finished/productized offerings vs. custom builds.
  appsGeneric:
    /\b(apps & products|apps and products|see your products|your studio|what do you sell|what do you offer|what apps|what products|what software|what tools)\b/,

  // ── Friendly small talk — short greeting deserves a short greeting. ─
  greeting:
    /^(hi|hey|hello|yo|sup|howdy|good (morning|afternoon|evening)|gm|ga|ge|what'?s up|whats up|wassup)[\s.!?]*$/i,

  // ── Affirmation / yes — typically follows a question; keep the
  // conversation moving rather than launching a new spiel.
  affirmation: /^(yes|yep|yeah|yup|sure|ok|okay|sounds good|please do|go for it|do it)[\s.!?]*$/i,
};

export const stubRouteChat = ({ message, history }: ChatRouteInput): ChatRouteOutput => {
  const m = message.toLowerCase().trim();
  const turn = history.length;

  // ── 0. First-turn / empty-message greeting ────────────────────────────
  // Hardcoded greeting reply on the very first turn — gives the widget
  // a confident opener instead of a generic "How can I help?".
  // Chip order intentionally mirrors the Services panel order on /:
  // App Development → Intelligent Agents → Web Design → Operations
  // Consulting, then Apps & Products as the 5th option pointing at
  // /studio. Visitors who scroll the home page first see the same
  // sequence inside the chat, so the mental model stays consistent.
  if (turn === 0 && m.length === 0) {
    return {
      reply:
        "Hey, I'm the BRANDCENTRAL assistant, trained on Samuel's playbooks. Tell me what you're working on and I'll point you to the right thing, or loop Samuel in directly. Or pick one of these:",
      actions: [
        { label: 'App Development', href: '/app-development' },
        { label: 'AI Agents', href: '/intelligent-agents' },
        { label: 'Web Design', href: '/web-design' },
        { label: 'Operations Consulting', href: '/operations-consulting' },
        { label: 'Apps & Products', href: '/studio' },
      ],
      routedTo: 'router',
      confidence: 1,
    };
  }

  // ── 1. Escalation (highest priority — checked before topic routing) ─
  if (RX.escalation.test(m)) {
    return {
      reply:
        "Got it. I'll bring Samuel in. Drop your email and he'll respond personally within a few hours, or book your 15-minute walkthrough right now.",
      actions: [
        { label: 'Email me back', action: 'escalate' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
        { label: 'Send a message', action: 'open-contact-form' },
      ],
      routedTo: 'escalation',
      shouldEscalate: true,
      confidence: 0.95,
    };
  }

  // ── 2. Friendly greetings — short reply, surface the same opener chips
  if (RX.greeting.test(m)) {
    return {
      reply:
        "Hey, what are you working on? I can point you to the right thing, or jump on a quick call.",
      actions: [
        { label: 'App Development', href: '/app-development' },
        { label: 'AI Agents', href: '/intelligent-agents' },
        { label: 'Web Design', href: '/web-design' },
        { label: 'Operations Consulting', href: '/operations-consulting' },
        { label: 'Apps & Products', href: '/studio' },
      ],
      routedTo: 'router',
      confidence: 0.9,
    };
  }

  // ── 3. "How does your process work?" — cross-service answer ──────────
  if (RX.process.test(m)) {
    return {
      reply:
        "Four steps: Walkthrough (15 minutes on where your processes slip), Blueprint (map the roles and tools), Build (turn on your AI Staff and connect your site, inbox and calendar), Run (AuraOS runs it day to day, every draft waits for your approval).",
      actions: [
        { label: 'Read the process', href: '/#process' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'router',
      confidence: 0.85,
    };
  }

  // ── 4. Location / GEO question ───────────────────────────────────────
  if (RX.location.test(m)) {
    return {
      reply:
        "We're in Mooresville, NC, on-site work available across the Charlotte metro and Lake Norman area (Cornelius, Davidson, Huntersville, Concord, Statesville). Remote for clients anywhere in the US.",
      actions: [
        { label: 'About us', href: '/#about' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'router',
      confidence: 0.9,
    };
  }

  if (RX.studioYoutube.test(m) && !/\bpost(ing)? (to|on)\b/.test(m)) {
    return {
      reply:
        "Not from YouTube links yet. Send the file, or share it from Google Drive or Dropbox, and we cut the Shorts from there; a shared folder works for every new recording.",
      actions: [
        { label: 'See AuraOS', href: '/auraos' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'studio',
      confidence: 0.92,
    };
  }

  if (RX.studioPrice.test(m)) {
    return {
      reply:
        "It comes with the content AI Staff: one AI Staff member is $800/mo (for example Video & Short-Form), and the full system starts at $2,500/mo. We scope it on the walkthrough.",
      actions: [
        { label: 'See pricing', href: '/auraos#pricing' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'studio',
      confidence: 0.92,
    };
  }

  if (RX.studio2.test(m) || RX.studioPost.test(m) || RX.studioEdit.test(m) || RX.studioCarousel.test(m)) {
    const reply = RX.studioPost.test(m)
      ? "Yes, once we connect your accounts with you: videos go to YouTube, Instagram and TikTok, and posts to LinkedIn and the other main platforms. Nothing goes out until you approve it."
      : RX.studioEdit.test(m)
        ? "Yes. Upload a recording and the AI edits it for you, Short or Long-form. From a long video (up to 3 hours) it cuts up to 12 Shorts, each edited on its own, and you approve each one. Or drop your recordings into a shared Google Drive or Dropbox folder and every new one is cut into Shorts automatically."
        : RX.studioCarousel.test(m)
          ? "Yes: five slide frameworks, written in your voice and turned into a LinkedIn-ready PDF for you to approve."
          : "It's the part of AuraOS where your content AI Staff make your videos and posts. Send in a recording and it comes back edited: dead air cut, the strongest line first, captions, graphics and a clean voice, ready for you to approve.";
    return {
      reply,
      actions: [
        { label: RX.studioEdit.test(m) ? 'Meet the AI Staff' : 'See AuraOS', href: RX.studioEdit.test(m) ? '/auraos#staff' : '/auraos' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'studio',
      confidence: 0.93,
    };
  }

  if (RX.counts.test(m)) {
    return {
      reply: /workflow/.test(m)
        ? "360+ workflows automated, at 2.8× average efficiency against the manual baseline."
        : "65+ AI Staff are deployed across our clients' businesses today, drawn from 40+ roles you can choose from (plus custom roles when you need one).",
      actions: [
        { label: 'See the AI Staff', href: '/auraos#staff' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'router',
      confidence: 0.9,
    };
  }

  if (RX.dashboard.test(m)) {
    return {
      reply:
        "Not exactly: your dashboard shows the AI Staff you turn on, so it changes with the roles you activate. The one on our site is one sample setup.",
      actions: [
        { label: 'See it in action', href: '/auraos#watch' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'router',
      confidence: 0.9,
    };
  }

  if (RX.reskin.test(m)) {
    return {
      reply:
        "Yes. All our services run through AuraOS, our own software, and we will also design a site, re-skin your current one or host it on Webflow, Framer, Shopify or Next.js if you don't want AuraOS full management. Standard rates either way ($450/page). Full management includes hosting, updates, SEO, reporting, audits, performance, security and more.",
      actions: [
        { label: 'See Web Design', href: '/web-design' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'web-design',
      confidence: 0.9,
    };
  }

  // ── 5. Studio specialty checks — AIOS (flagship) first, then LegalLio +
  //      CRM, then the generic studio catch-all so product-specific
  //      keywords get the product-specific reply instead of the broad card.
  if (RX.aios.test(m)) {
    return {
      reply:
        "That's AuraOS, our AI Operating Foundation. AI staff each work a role (leads, content, onboarding, support, invoicing) and everything they produce lands in an approval queue, so nothing ships without your yes. All our services run through it. The full system starts at $2,500/mo for AuraOS plus your web, apps, and agents as one system, with 50% off your first 90 days. Or start with one AI Staff member for $800/mo.",
      actions: [
        { label: 'See AuraOS', href: '/auraos' },
        { label: 'See pricing', href: '/auraos#pricing' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'studio',
      confidence: 0.93,
    };
  }

  if (RX.legallio.test(m)) {
    return {
      reply:
        "Contracts and NDAs are handled by AuraOS's Contracts & Legal AI Staff (Malee): she drafts from your terms, and every draft needs licensed review before it is sent. Nothing goes out until you approve it.",
      actions: [
        { label: 'See the AI Staff', href: '/auraos#staff' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'studio',
      confidence: 0.92,
    };
  }

  if (RX.crm.test(m)) {
    return {
      reply:
        "That's BRANDCENTRAL CRM, a CRM with AI agents and automation built in, designed for digital agencies, AI agencies, and operations teams. Starter $20/user/mo, Professional $39/user/mo (AI agents unlocked, most popular), Enterprise custom. It's live now.",
      actions: [
        { label: 'See the CRM', href: '/studio/brandcentral-crm' },
        { label: 'Open app', href: 'https://crm.brandctl.com' },
      ],
      routedTo: 'studio',
      confidence: 0.92,
    };
  }

  if (RX.studio.test(m) || RX.appsGeneric.test(m)) {
    return {
      reply:
        "Our studio ships BRANDCENTRAL CRM (live, $20–$39/user/mo), and the flagship is AuraOS, our AI Operating Foundation. More products are in active development; join the early-access list on the studio page.",
      actions: [
        { label: 'See Studio', href: '/studio' },
        { label: 'AuraOS', href: '/auraos' },
        { label: 'BRANDCENTRAL CRM', href: '/studio/brandcentral-crm' },
      ],
      routedTo: 'studio',
      confidence: 0.88,
    };
  }

  // ── 6. Operations consulting ────────────────────────────────────────
  if (RX.ops.test(m)) {
    return {
      reply:
        "That's the $497 consulting call: 1 hour live + written blueprint within 5 business days identifying your three highest-leverage bottlenecks. The blueprint is yours to keep either way, and the $497 credits toward the first invoice if you move forward.",
      actions: [
        { label: 'Read the details', href: '/operations-consulting' },
        { label: 'Book the call', action: 'open-cal-consulting' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'operations-consulting',
      confidence: 0.92,
    };
  }

  // ── 7. AI agents ────────────────────────────────────────────────────
  if (RX.aiAgents.test(m)) {
    return {
      reply:
        "Yes. Our agents run as AuraOS AI Staff: each works a role (lead triage, follow-ups, content, intake) and its work waits for your approval. One AI Staff member is $800/mo, the full system starts at $2,500/mo. For a one-off custom build into your own stack, that's quote-based. What workflow is eating the most time?",
      actions: [
        { label: 'See AuraOS', href: '/auraos' },
        { label: 'Custom builds', href: '/intelligent-agents' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'ai-agents',
      confidence: 0.92,
    };
  }

  // ── 8. App development ──────────────────────────────────────────────
  if (RX.appDev.test(m)) {
    return {
      reply:
        "Custom web + mobile app development: cross-platform by default (React Native / Expo); native Swift or Kotlin on request. Projects start at $3,000 for a focused MVP; $15-30k for production-grade with auth + payments. Typical timeline 4-12 weeks.",
      actions: [
        { label: 'See App Development', href: '/app-development' },
        { label: 'Get a quote', action: 'open-contact-form', lockedService: 'App Development' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'app-development',
      confidence: 0.92,
    };
  }

  // ── 9. Web design ───────────────────────────────────────────────────
  if (RX.webDesign.test(m)) {
    return {
      reply:
        "$450 per page, 1-50+ pages, flat. Optional content writing (+$75/page) and on-page SEO (+$75/page) are toggles in the intake form. Vite + React by default, or we design, re-skin or host on Webflow, Framer, Shopify or Next.js if you prefer your own platform. Typical timeline 2-6 weeks.",
      actions: [
        { label: 'See Web Design', href: '/web-design' },
        { label: 'Get a quote', action: 'open-contact-form', lockedService: 'Web Design' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'web-design',
      confidence: 0.92,
    };
  }

  // ── 10. Timeline question without a specific service mentioned ──────
  if (RX.timeline.test(m)) {
    return {
      reply:
        "Depends on what we're building. Web design: 2-6 weeks. App development: 4-12 weeks. AI agents: 4-8 weeks. Operations consulting: blueprint in 5 business days, build phase 4-8 weeks. Which one fits?",
      actions: [
        { label: 'Web Design', href: '/web-design' },
        { label: 'App Development', href: '/app-development' },
        { label: 'AI Agents', href: '/intelligent-agents' },
        { label: 'Ops Consulting', href: '/operations-consulting' },
      ],
      routedTo: 'router',
      confidence: 0.8,
    };
  }

  // ── 11. Pricing question without a specific service mentioned ───────
  if (RX.pricing.test(m)) {
    return {
      reply:
        "The full system starts at $2,500/mo: AuraOS plus your web, apps, and agents as one system, with 50% off your first 90 days (registered nonprofits keep $500/mo off for life). Or start with one AI Staff member, any role, for $800/mo. Web Design is $450/page flat, App Development from $3,000, and the Operations Consulting deep dive is $497. We scope it on the walkthrough.",
      actions: [
        { label: 'See AuraOS pricing', href: '/auraos#pricing' },
        { label: 'Web Design', href: '/web-design' },
        { label: 'Ops Consulting', href: '/operations-consulting' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'router',
      confidence: 0.8,
    };
  }

  // ── 12. Booking / scheduling intent (after specific service checks) ──
  if (RX.booking.test(m)) {
    return {
      reply:
        "Three options: the free 15-minute walkthrough of AuraOS (no slide deck, no obligation), a free 15-minute call if you just have questions or want more info, or the $497 consulting call (1 hour live + written blueprint). Which one feels right?",
      actions: [
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
        { label: 'Book the $497 consulting call', action: 'open-cal-consulting' },
      ],
      routedTo: 'router',
      confidence: 0.8,
    };
  }

  // ── 13. Affirmation handling — short "yes" without context becomes a
  //       prompt for more detail rather than a non-sequitur. ───────────
  if (RX.affirmation.test(m)) {
    return {
      reply:
        "Want to tell me a bit more about what you're working on? I'll send you to the right page or set up a call.",
      actions: [
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
        { label: 'Email me directly', action: 'escalate' },
      ],
      routedTo: 'router',
      confidence: 0.55,
    };
  }

  // ── 14. Multi-turn low-confidence fallback → suggest escalation ─────
  if (turn >= 4) {
    return {
      reply:
        "I want to make sure I send you the right way. Want me to follow up by email so a human can answer this properly?",
      actions: [
        { label: 'Email me back', action: 'escalate' },
        { label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' },
        { label: 'Free 15-min call for questions', action: 'open-cal-15min' },
      ],
      routedTo: 'escalation',
      shouldEscalate: true,
      confidence: 0.4,
    };
  }

  // ── 15. Default — broad reply with chips ─────────────────────────────
  return {
    reply:
      "Tell me a bit more: are you looking for a custom build (web design, AI agents, app development, ops consulting), or a finished product from our studio (AuraOS or BRANDCENTRAL CRM)?",
    actions: [
      { label: 'Custom build', href: '/#services' },
      { label: 'Finished product', href: '/studio' },
      { label: 'Talk to a human', action: 'escalate' },
    ],
    routedTo: 'router',
    confidence: 0.5,
  };
};

// Maximum allowable shape of an action returned from the agent. We use a
// runtime allowlist (rather than trusting TypeScript at the boundary) so
// a malformed agent reply can't smuggle an arbitrary `action: 'redirect'`
// or unsafe `href` into the widget.
const VALID_ACTION_KINDS = new Set([
  'open-contact-form',
  'escalate',
  'open-cal-15min',
  'open-cal-consulting',
  'open-cal-aios',
]);
const VALID_LOCKED_SERVICES = new Set([
  'App Development',
  'Intelligent Agents',
  'Web Design',
  'Operations Consulting',
]);

export const sanitizeChatRouteOutput = (raw: unknown): ChatRouteOutput | null => {
  if (!raw || typeof raw !== 'object') return null;
  let r = raw as Record<string, unknown>;

  // Some agent setups return the
  // structured response wrapped in a ```json ... ``` markdown code
  // block as a single string instead of as a structured object. This
  // happens when the agent's system prompt says "return JSON" but the
  // Output Schema isn't forcing structured output. Unwrap defensively
  // so the chat still works while you fix the agent's schema. The
  // canonical fix is to set the Output Schema on every chat agent so
  // the platform serializes responses directly. See
  // docs/CHAT_AGENT_SETUP.md § Output Schema.
  const stringCandidate =
    typeof r.reply === 'string'
      ? r.reply
      : typeof r.answer === 'string'
        ? r.answer
        : '';
  if (
    stringCandidate.trim().startsWith('```json') ||
    stringCandidate.trim().startsWith('```')
  ) {
    const unwrapped = stringCandidate
      .trim()
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/, '')
      .replace(/```$/, '')
      .trim();
    try {
      const parsed = JSON.parse(unwrapped);
      if (parsed && typeof parsed === 'object') {
        r = parsed as Record<string, unknown>;
      }
    } catch {
      // Not parseable JSON — fall through and treat as plain reply text.
    }
  }

  // Accept BOTH the documented field names (reply, actions, label) and
  // the common alternates (answer, chips, text). Tolerating both keeps
  // the widget working if the agent's output drifts from the spec.
  const replyText =
    typeof r.reply === 'string' && r.reply.length > 0
      ? r.reply
      : typeof r.answer === 'string' && r.answer.length > 0
        ? r.answer
        : '';
  if (!replyText) return null;

  const rawActions = Array.isArray(r.actions)
    ? r.actions
    : Array.isArray(r.chips)
      ? r.chips
      : [];
  const actions = rawActions
    .filter((a): a is Record<string, unknown> => !!a && typeof a === 'object')
    .map((a) => {
      const labelRaw =
        typeof a.label === 'string'
          ? a.label
          : typeof a.text === 'string'
            ? a.text
            : '';
      const label = labelRaw.slice(0, 80);
      if (!label) return null;
      const href = typeof a.href === 'string' ? a.href : undefined;
      const action =
        typeof a.action === 'string' && VALID_ACTION_KINDS.has(a.action)
          ? (a.action as
              | 'open-contact-form'
              | 'escalate'
              | 'open-cal-15min'
              | 'open-cal-consulting'
              | 'open-cal-aios')
          : undefined;
      const lockedServiceRaw =
        typeof a.lockedService === 'string' ? a.lockedService : undefined;
      const lockedService =
        lockedServiceRaw && VALID_LOCKED_SERVICES.has(lockedServiceRaw)
          ? lockedServiceRaw
          : undefined;
      // Disallow `javascript:` / data URLs that could execute or exfiltrate
      // when the widget routes a chip click. Only allow http(s) and
      // in-app absolute paths.
      const hrefSafe =
        href && (href.startsWith('/') || /^https?:\/\//i.test(href)) ? href : undefined;
      if (!hrefSafe && !action) return null; // must have one or the other
      return { label, href: hrefSafe, action, lockedService };
    })
    .filter((a): a is NonNullable<typeof a> => a !== null)
    .slice(0, 5); // hard cap so the agent can't blow out the UI

  return {
    reply: replyText.slice(0, 4000), // cap reply length so a runaway agent can't paste mb's
    actions,
    routedTo:
      typeof r.routedTo === 'string'
        ? (r.routedTo as ChatRouteOutput['routedTo'])
        : undefined,
    shouldEscalate: r.shouldEscalate === true,
    confidence:
      typeof r.confidence === 'number' && Number.isFinite(r.confidence)
        ? Math.max(0, Math.min(1, r.confidence))
        : undefined,
  };
};

/** Promise.race against a timer; if the timer wins, reject the call. */
export const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`timeout after ${ms}ms`)), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });

// AIOS chat measured ~15s end-to-end on a real question (2026-09-26); the
// endpoint itself allows 60s. 30s keeps real answers from being dropped for
// the stub while still bounding a hung request.
export const CHAT_AGENT_TIMEOUT_MS = 30_000;
export const HISTORY_CAP = 20; // last N turns sent to the agent — keeps payload small
